import { Route, Routes } from 'react-router-dom';
import { Suspense, lazy } from 'react';
import { LoginCallback } from '@okta/okta-react';
import Spinner from './common/components/Spinner';
import { RequireAuthorization } from './views/RequireAuthorization';

const Login = lazy(() => import('./views/Public/Login'));
const Unauthorized = lazy(() => import('./views/Public/Unauthorized'));
const Forbidden = lazy(() => import('./views/Public/Forbidden'));
const PageNotFound = lazy(() => import('./views/Public/PageNotFound'));
const Problem = lazy(() => import('./views/Public/Problem'));

const MyTaskPanel = lazy(() => import('./views/MyTasks/MyTaskPanel'));
const MyCheckLists = lazy(() => import('./views/MyCheckLists/MyCheckLists'));
const MyCheckListPanel = lazy(() =>
  import('./views/MyCheckLists/MyCheckListPanel')
);
const MyCheckListUserTasks = lazy(() =>
  import('./views/MyCheckLists/MyCheckListUserTasks')
);
const MyCheckListBulkComments = lazy(() =>
  import('./views/MyCheckLists/MyCheckListBulkComments')
);
const TaskDashboard = lazy(() =>
  import('./views/MyCheckLists/TaskDashboard')
);
const Users = lazy(() => import('./views/Users/Users'));
const OktaUserAdd = lazy(() => import('./views/Users/OktaUserAdd'));
const UserAdd = lazy(() => import('./views/Users/UserAdd'));
const UserEdit = lazy(() => import('./views/Users/UserEdit'));
const UserRoles = lazy(() => import('./views/Users/UserRoles'));
const UserRoleAdd = lazy(() => import('./views/Users/UserRoles/UserRoleAdd'));
const UserPostRoles = lazy(() => import('./views/Users/UserPostRoles'));
const UserPostRoleAdd = lazy(() =>
  import('./views/Users/UserPostRoles/UserPostRoleAdd')
);
const CustomMissions = lazy(() => import('./views/CustomMissions/CustomMissions'));
const CustomMissionAdd = lazy(() => import('./views/CustomMissions/CustomMissionAdd'));
const CustomMissionEdit = lazy(() => import('./views/CustomMissions/CustomMissionEdit'));
const CopyCheckListBuilder = lazy(() =>
  import('./views/TemplateBuilder/CopyCheckListBuilder/CopyCheckListBuilder')
);
const BuildCheckList = lazy(() =>
  import('./views/TemplateBuilder/BuildCheckList/BuildCheckList')
);
const BuildCheckLists = lazy(() =>
  import('./views/TemplateBuilder/BuildCheckList/BuildCheckLists')
);
const BuildCheckListAdd = lazy(() =>
  import('./views/TemplateBuilder/BuildCheckList/BuildCheckListAdd')
);
const BuildCheckListEdit = lazy(() =>
  import('./views/TemplateBuilder/BuildCheckList/BuildCheckListEdit')
);
const BuildGroupDefinitionAdd = lazy(() =>
  import('./views/TemplateBuilder/BuildGroupDefinition/BuildGroupDefinitionAdd')
);
const BuildGroupDefinitionEdit = lazy(() =>
  import(
    './views/TemplateBuilder/BuildGroupDefinition/BuildGroupDefinitionEdit'
  )
);
const BuildTaskDefinitionAdd = lazy(() =>
  import('./views/TemplateBuilder/BuildTaskDefinition/BuildTaskDefinitionAdd')
);
const BuildTaskDefinitionEdit = lazy(() =>
  import('./views/TemplateBuilder/BuildTaskDefinition/BuildTaskDefinitionEdit')
);
const BuildTaskDefinitionDelete = lazy(() =>
  import(
    './views/TemplateBuilder/BuildTaskDefinition/BuildTaskDefinitionDelete'
  )
);
const BuildTaskDefinitions = lazy(() =>
  import('./views/TemplateBuilder/BuildTaskDefinition/BuildTaskDefinitions')
);
const BuildTaskDefinitionPanel = lazy(() =>
  import('./views/TemplateBuilder/BuildTaskDefinition/BuildTaskDefinitionPanel')
);
const SearchTaskDefinitions = lazy(() =>
  import('./views/TemplateBuilder/BuildTaskDefinition/SearchTaskDefinitions')
);
const BuildTaskLinkAdd = lazy(() =>
  import('./views/TemplateBuilder/BuildTaskLinks/BuildTaskLinkAdd')
);
const BuildTaskLinkEdit = lazy(() =>
  import('./views/TemplateBuilder/BuildTaskLinks/BuildTaskLinkEdit')
);
const BuildTaskLinks = lazy(() =>
  import('./views/TemplateBuilder/BuildTaskLinks/BuildTaskLinks')
);
const BuildTaskDocuments = lazy(() =>
  import('./views/TemplateBuilder/BuildTaskDocuments/BuildTaskDocuments')
);
const BuildTaskDocumentAdd = lazy(() =>
  import('./views/TemplateBuilder/BuildTaskDocuments/BuildTaskDocumentAdd')
);
const BuildTaskDocumentEdit = lazy(() =>
  import('./views/TemplateBuilder/BuildTaskDocuments/BuildTaskDocumentEdit')
);
const BuildTaskTags = lazy(() =>
  import('./views/TemplateBuilder/BuildTaskTags/BuildTaskTags')
);

const AppRoutes = () => {
  return (
    <Suspense fallback={<Spinner />}>
      <Routes>
        <Route path="/" element={<Login />} />
        <Route path="/authorization-code/callback" element={<LoginCallback />} />
        <Route path="/unauthorized" element={<Unauthorized />} />
        <Route path="/forbidden" element={<Forbidden />} />
        <Route path="/pagenotfound" element={<PageNotFound />} />
        <Route path="/problem/:status/:message" element={<Problem />} />

        <Route
          path="/mytaskpanel"
          element={<RequireAuthorization feature="my_tasks" />}
        >
          <Route index element={<MyTaskPanel />} />
        </Route>
        <Route
          path="/mychecklists"
          element={<RequireAuthorization feature="my_check_lists" />}
        >
          <Route index element={<MyCheckLists />} />
          <Route
            path="mychecklistPanel"
            element={<RequireAuthorization feature="my_check_lists" />}
          >
            <Route path=":id" element={<MyCheckListPanel />} />
            <Route
              path="mychecklistusertasks"
              element={<RequireAuthorization feature="my_check_lists" />}
            >
              <Route path=":id" element={<MyCheckListUserTasks />} />
            </Route>
            <Route
              path="mychecklistbulkcomments"
              element={<RequireAuthorization feature="my_check_lists" />}
            >
              <Route path=":id" element={<MyCheckListBulkComments />} />
            </Route>
            <Route
              path="taskDashboard"
              element={<RequireAuthorization feature="my_check_lists" />}
            >
              <Route path=":id" element={<TaskDashboard />} />
            </Route>
          </Route>
        </Route>
        <Route
          path="/copychecklistbuilder/:id/:name/:version"
          element={<RequireAuthorization feature="builder_roles" />}
        >
          <Route index element={<CopyCheckListBuilder />} />
        </Route>
        <Route
          path="/buildchecklists"
          element={<RequireAuthorization feature="builder_roles" />}
        >
          <Route index element={<BuildCheckLists />} />
          <Route
            path="buildchecklist"
            element={<RequireAuthorization feature="builder_roles" />}
          >
            <Route path=":id" element={<BuildCheckList />} />
            <Route path="add" element={<BuildCheckListAdd />} />
            <Route path=":id/edit" element={<BuildCheckListEdit />} />
            <Route
              path="buildgroupdefinition"
              element={<RequireAuthorization feature="builder_roles" />}
            >
              <Route
                path=":checkListBuilderId/:parentId/add"
                element={<BuildGroupDefinitionAdd />}
              />
              <Route path=":id/edit" element={<BuildGroupDefinitionEdit />} />
            </Route>
            <Route
              path="searchtaskdefinitions/:groupDefinitionBuilderId/:checkListBuilderId"
              element={<RequireAuthorization feature="builder_roles" />}
            >
              <Route index element={<SearchTaskDefinitions />} />
            </Route>
          </Route>
        </Route>
        <Route 
          path="/custommissions"
          element={<RequireAuthorization feature="global_roles" />}
        >
          <Route index element={<CustomMissions />} />
        </Route>
        <Route path="custommission/add"         
          element={<RequireAuthorization feature="global_roles" />}
        >
          <Route index element={<CustomMissionAdd />} />
        </Route>
          <Route path="custommission/:id/edit" element={<CustomMissionEdit />} />
        <Route
          path="/users"
          element={<RequireAuthorization feature="mission_roles" />}
        >
          <Route index element={<Users />} />
          <Route
            path="okta"
            element={<RequireAuthorization feature="adduser_roles" />}
          >
            <Route path="add" element={<OktaUserAdd isAllUsers={true} />} />
          </Route>
          <Route
            path="all"
            element={<RequireAuthorization feature="global_roles" />}
          >
            <Route index element={<Users />} />
          </Route>
        </Route>
        <Route
          path="/user"
          element={<RequireAuthorization feature="adduser_roles" />}
        >
          <Route path="add" element={<UserAdd isAllUsers={true} />} />
          <Route path="okta/add" element={<OktaUserAdd isAllUsers={true} />} />
        </Route>
        <Route
          path="/user/:id/edit"
          element={<RequireAuthorization feature="mission_roles" />}
        >
          <Route index element={<UserEdit isAllUsers={true} />} />
        </Route>
        <Route
          path="/user/:id/:isAllUsersPath/roles"
          element={<RequireAuthorization feature="mission_roles" />}
        >
          <Route index element={<UserRoles />} />
        </Route>
        <Route
          path="/user/:id/roles/add"
          element={<RequireAuthorization feature="mission_roles" />}
        >
          <Route index element={<UserRoleAdd />} />
        </Route>
        <Route
          path="/user/:id/:isAllUsersPath/postroles"
          element={<RequireAuthorization feature="mission_roles" />}
        >
          <Route index element={<UserPostRoles />} />
        </Route>
        <Route
          path="/user/:id/:isAllUsersPath/postroles/add"
          element={<RequireAuthorization feature="mission_roles" />}
        >
          <Route index element={<UserPostRoleAdd />} />
        </Route>

        <Route
          path="/buildtaskdefinitionpanel"
          element={<RequireAuthorization feature="builder_roles" />}
        >
          <Route index element={<BuildTaskDefinitionPanel />} />
        </Route>  
        <Route
          path="/buildtaskdefinitions"
          element={<RequireAuthorization feature="builder_roles" />}
        >
          <Route index element={<BuildTaskDefinitions />} />
          <Route
            path="buildtaskdefinition"
            element={<RequireAuthorization feature="builder_roles" />}
          >
            <Route path="add" element={<BuildTaskDefinitionAdd />} />
            <Route path=":id/edit" element={<BuildTaskDefinitionEdit />} />
            <Route path=":id/delete" element={<BuildTaskDefinitionDelete />} />
          </Route>
          <Route
            path="buildtasktags/:id"
            element={<RequireAuthorization feature="builder_roles" />}
          >
            <Route index element={<BuildTaskTags />} />
          </Route>
          <Route
            path="buildtasklinks"
            element={<RequireAuthorization feature="builder_roles" />}
          >
            <Route path=":id" element={<BuildTaskLinks />} />
            <Route
              path="buildtasklink"
              element={<RequireAuthorization feature="builder_roles" />}
            >
              <Route path=":id/add" element={<BuildTaskLinkAdd />} />
              <Route path=":id/edit" element={<BuildTaskLinkEdit />} />
            </Route>
          </Route>
          <Route
            path="buildtaskdocuments"
            element={<RequireAuthorization feature="builder_roles" />}
          >
            <Route path=":id" element={<BuildTaskDocuments />} />
            <Route
              path="buildtaskdocument"
              element={<RequireAuthorization feature="builder_roles" />}
            >
              <Route path=":id/add" element={<BuildTaskDocumentAdd />} />
              <Route path=":id/edit" element={<BuildTaskDocumentEdit />} />
            </Route>
          </Route>
        </Route>  
      </Routes>
    </Suspense>
  );
};

export default AppRoutes;
